import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { api, formatDateTime, mensajeError } from '../../lib/api';
import { IndicadorSeguimiento } from '../IndicadoresSeguimiento';

/**
 * EL APARTADO «SEGUIMIENTOS» DE LA FICHA (administración, 4/11/2026)
 * ------------------------------------------------------------------
 * Todos los episodios de seguimiento del paciente, del más nuevo al más
 * antiguo: fecha y hora (las del servidor), a quién estaba asignado, quién lo
 * escribió y la nota. Se escriben con el botón «Seguimiento» de la cabecera
 * (BotonesSeguimientoCaution); aquí solo se leen: son historial.
 *
 * Desde el menú «Seguimientos» se llega con `#seguimientos` en la dirección:
 * el apartado se coloca a la vista solo.
 *
 * Sin ningún episodio, no ocupa sitio (salvo que se llegue a él por el
 * enlace: entonces dice que no hay ninguno). Un fallo de lectura se dice: no
 * se enseña como «no hay ninguno».
 */
export const ANCLA_SEGUIMIENTOS = 'seguimientos';

export default function SeguimientosFicha({ patientId, recarga = 0 }) {
  const location = useLocation();
  const porEnlace = (location?.hash || '') === `#${ANCLA_SEGUIMIENTOS}`;
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  const ref = useRef(null);
  const colocado = useRef(false);

  useEffect(() => {
    if (!patientId) return undefined;
    let vivo = true;
    setError('');
    api.get(`/api/seguimientos/pacientes/${patientId}`)
      .then((r) => { if (vivo) setDatos(r.data || {}); })
      .catch((e) => {
        if (!vivo) return;
        // El 403 se calla: a quien no puede ver la ficha no se le enseña.
        if (e?.response?.status === 403) { setDatos({ episodios: [] }); return; }
        setError(mensajeError(e, 'No se han podido leer los seguimientos'));
      });
    return () => { vivo = false; };
  }, [patientId, recarga]);

  useEffect(() => {
    if (!porEnlace || colocado.current || (!datos && !error) || !ref.current) return;
    colocado.current = true;
    try { ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch { /* jsdom */ }
  }, [porEnlace, datos, error]);

  const episodios = datos?.episodios || [];
  const seguimiento = datos?.seguimiento || null;
  if (!error && datos && !episodios.length && !porEnlace) return null;
  if (!error && !datos && !porEnlace) return null;

  return (
    <section
      id={ANCLA_SEGUIMIENTOS}
      ref={ref}
      className="cjo-card p-5 mb-6 scroll-mt-4"
      data-testid="ficha-seguimientos"
      aria-labelledby="ficha-seguimientos-titulo"
    >
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h3 id="ficha-seguimientos-titulo" className="font-display font-semibold text-[#13231E] text-base">
          Seguimientos ({episodios.length})
        </h3>
        {seguimiento?.estado === 'en_seguimiento' && <IndicadorSeguimiento tamano="pequeno" testId="ficha-seguimientos-activo" />}
        {seguimiento?.estado === 'completado' && (
          <span className="text-xs text-muted-foreground" data-testid="ficha-seguimientos-completado">
            Seguimiento completado el {formatDateTime(seguimiento.completado_at)}
            {seguimiento.completado_por_nombre ? ` por ${seguimiento.completado_por_nombre}` : ''}
          </span>
        )}
      </div>
      {error ? (
        <p className="text-sm text-red-700" data-testid="ficha-seguimientos-error">
          {error}. Esto no quiere decir que no tenga seguimientos.
        </p>
      ) : !datos ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : !episodios.length ? (
        <p className="text-sm text-muted-foreground">Este paciente no tiene ningún seguimiento.</p>
      ) : (
        <ol className="space-y-2" data-testid="ficha-seguimientos-lista">
          {episodios.map((e) => (
            <li key={e.id} className="rounded-md border border-border px-3 py-2" data-testid="ficha-seguimiento-episodio">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                <span className="font-semibold text-[#13231E] tabular-nums">{formatDateTime(e.at)}</span>
                <span>▸ Asignado a {e.asignado_a_nombre || 'nadie'}</span>
                <span>▸ Escrito por {e.autor_nombre || '—'}</span>
              </div>
              <p className="mt-1 text-sm text-[#13231E] whitespace-pre-wrap break-words">{e.nota}</p>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
